<template>
  <div v-if="hasAccess" class="meetings-page">
    <page-content class="meetings-page__content">
      <section class="meetings-panel">
        <header class="meetings-header">
          <div class="meetings-header__main">
            <router-link :to="{ name: 'app-meetings' }" class="meetings-back">
              <i class="fas fa-arrow-left" aria-hidden="true"></i> Réunions CSE
            </router-link>
            <h2 class="meetings-header__title">Dossier de suivi du CSE</h2>
            <p class="meetings-header__subtitle">
              La mémoire du CSE d’une réunion à l’autre : points ouverts, décisions récentes, contexte durable,
              actualités déjà proposées. La rédaction de chaque réunion le réécrit ; corrigez-le ici quand il se trompe,
              la prochaine rédaction repartira de votre version.
            </p>
          </div>
          <div class="meetings-header__actions">
            <template v-if="editing">
              <button type="button" class="meetings-outline" :disabled="saving" @click="cancelEdit">Annuler</button>
              <button type="button" class="meetings-action context-save" :disabled="saving || !draft.trim()" @click="save">
                <i :class="saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'" aria-hidden="true"></i>
                Enregistrer comme nouvelle version
              </button>
            </template>
            <template v-else-if="viewing">
              <button type="button" class="meetings-outline" @click="viewing = null">Revenir à la version courante</button>
              <button type="button" class="meetings-outline meetings-outline--primary" :disabled="saving" @click="restore">
                <i :class="saving ? 'fas fa-spinner fa-spin' : 'fas fa-history'" aria-hidden="true"></i>
                Restaurer cette version
              </button>
            </template>
            <button v-else-if="current" type="button" class="meetings-outline meetings-outline--primary" @click="startEdit">
              <i class="fas fa-pen" aria-hidden="true"></i> Corriger le dossier
            </button>
          </div>
        </header>

        <div v-if="loading" class="meetings-state">
          <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Chargement du dossier…
        </div>
        <div v-else-if="loadError" class="meetings-state meetings-state--error">
          <span>{{ loadError }}</span>
          <button type="button" @click="load()">Réessayer</button>
        </div>

        <div v-else class="context-body">
          <div v-if="!current && !editing" class="meetings-state context-empty">
            <p class="context-empty__title">Aucun dossier de suivi pour ce CSE.</p>
            <p>
              Il sera créé par la rédaction de la première réunion déposée. Vous pouvez aussi le commencer à la main,
              par exemple avec les points en cours et les décisions récentes.
            </p>
            <button type="button" class="meetings-outline meetings-outline--primary" @click="startEdit">
              <i class="fas fa-pen" aria-hidden="true"></i> Commencer le dossier
            </button>
          </div>

          <template v-else>
            <div v-if="shown" class="context-version-line">
              <span class="context-version-line__label">{{ viewing ? 'Version consultée' : 'Version courante' }}</span>
              <span>{{ versionLabel(shown) }}</span>
              <span v-if="shown.word_count" class="context-version-line__words">{{ formatNumber(shown.word_count) }} mots</span>
            </div>

            <div v-if="editing" class="context-editor">
              <textarea
                v-model="draft"
                class="context-editor__textarea"
                :maxlength="MAX_CHARACTERS"
                spellcheck="true"
                aria-label="Texte du dossier de suivi"
              ></textarea>
              <p class="context-editor__hint">
                Markdown. Gardez les quatre sections (Points ouverts, Décisions récentes, Contexte durable,
                Actualités déjà proposées) : la rédaction s’y retrouve. {{ formatNumber(draftWords) }} mots.
              </p>
            </div>
            <div v-else-if="shown && shown.text" class="md-content context-text" v-html="renderedText"></div><!-- eslint-disable-line vue/no-v-html -->
            <div v-else-if="viewing" class="meetings-state">
              <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Chargement de la version…
            </div>
          </template>

          <section v-if="versions.length" class="context-history">
            <h3 class="meetings-section-title">Versions</h3>
            <div class="meeting-table-wrap">
              <table class="meeting-table context-history__table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Origine</th>
                    <th>Taille</th>
                    <th><span class="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="version in versions" :key="version.id" :class="{ 'context-history__row--shown': shown && shown.id === version.id }">
                    <td>{{ formatDateTime(version.created_at) || '—' }}</td>
                    <td>{{ sourceLabel(version) }}</td>
                    <td>{{ version.word_count ? `${formatNumber(version.word_count)} mots` : '—' }}</td>
                    <td class="context-history__actions">
                      <button
                        v-if="current && version.id !== current.id"
                        type="button"
                        class="meetings-link-button"
                        :disabled="editing"
                        @click="view(version)"
                      >
                        Consulter
                      </button>
                      <span v-else class="context-history__current">courante</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </section>
    </page-content>
  </div>

  <div v-else class="meetings-no-access">
    <p>Vous n’avez pas accès à cette section.</p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { marked } from 'marked'
import PageContent from '@/components/PageContent.vue'
import aiService from '@/services/ai.service'
import { MEETING_ROLES, formatDateTime, formatMeetingDate, formatNumber } from './meetingHelpers'

/** Même limite que l'API (MeetingContextService::MAX_CHARACTERS). */
const MAX_CHARACTERS = 30000

/**
 * Dossier de suivi du CSE : la version courante rendue en Markdown, sa
 * correction à la main (qui crée une version), l'historique des versions et
 * la consultation ou la restauration d'une ancienne version.
 */
export default {
  name: 'app-meetings-context',
  components: { PageContent },
  data () {
    return {
      MAX_CHARACTERS,
      current: null,
      versions: [],
      viewing: null,
      editing: false,
      draft: '',
      loading: true,
      loadError: '',
      saving: false
    }
  },
  computed: {
    ...mapGetters(['roles', 'domain']),
    hasAccess () {
      return this.roles.some(role => MEETING_ROLES.includes(role))
    },
    /** Version affichée : l'ancienne consultée, sinon la courante. */
    shown () {
      return this.viewing || this.current
    },
    renderedText () {
      return this.shown && this.shown.text ? marked.parse(String(this.shown.text), { breaks: true }) : ''
    },
    draftWords () {
      const words = this.draft.trim().split(/\s+/).filter(Boolean)
      return words.length
    }
  },
  watch: {
    'domain.uuid' () {
      this.viewing = null
      this.editing = false
      this.load()
    }
  },
  created () {
    this.load()
  },
  methods: {
    formatDateTime,
    formatNumber,
    load () {
      if (!this.domain || !this.domain.uuid) {
        return
      }
      this.loading = true
      this.loadError = ''
      aiService.getMeetingContext().then(payload => {
        this.applyPayload(payload)
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        this.loadError = status === 403
          ? 'Vous n’avez pas le rôle requis pour consulter le dossier de suivi.'
          : 'Impossible de charger le dossier de suivi.'
      }).finally(() => {
        this.loading = false
      })
    },
    applyPayload (payload) {
      this.current = payload && payload.current ? payload.current : null
      this.versions = payload && Array.isArray(payload.versions) ? payload.versions : []
    },
    startEdit () {
      this.draft = this.shown && this.shown.text ? String(this.shown.text) : ''
      this.viewing = null
      this.editing = true
    },
    cancelEdit () {
      this.editing = false
      this.draft = ''
    },
    save () {
      this.submit(this.draft, 'Dossier de suivi enregistré : la prochaine rédaction repartira de cette version.')
    },
    restore () {
      if (!this.viewing || !this.viewing.text) {
        return
      }
      this.submit(this.viewing.text, 'Version restaurée : elle est redevenue la version courante.')
    },
    submit (text, successMessage) {
      if (this.saving || !String(text || '').trim()) {
        return
      }
      this.saving = true
      aiService.updateMeetingContext(text).then(payload => {
        this.applyPayload(payload)
        this.editing = false
        this.viewing = null
        this.draft = ''
        this.notify('is-success', successMessage)
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        this.notify('is-danger', status === 422
          ? 'Le dossier est vide ou trop long.'
          : (status === 403 ? 'Vous n’avez pas le rôle requis pour corriger le dossier.' : 'Impossible d’enregistrer le dossier.'))
      }).finally(() => {
        this.saving = false
      })
    },
    view (version) {
      this.viewing = Object.assign({}, version, { text: '' })
      aiService.getMeetingContextVersion(version.id).then(full => {
        if (this.viewing && this.viewing.id === version.id) {
          this.viewing = full
        }
      }).catch(() => {
        this.viewing = null
        this.notify('is-danger', 'Impossible de charger cette version.')
      })
    },
    sourceLabel (version) {
      if (version.source === 'manual') {
        return version.author ? `Corrigé par ${version.author}` : 'Corrigé à la main'
      }
      if (version.meeting) {
        const date = formatMeetingDate(version.meeting.meeting_date)
        return `Rédaction de « ${version.meeting.title} »${date ? ` (${date})` : ''}`
      }
      return 'Rédaction d’une réunion supprimée'
    },
    versionLabel (version) {
      const when = formatDateTime(version.created_at)
      return `${this.sourceLabel(version)}${when ? `, le ${when}` : ''}`
    },
    notify (type, message) {
      this.$buefy.notification.open({
        duration: 4000,
        message,
        position: 'is-bottom-right',
        type,
        hasIcon: false
      })
    }
  }
}
</script>

<style src="./meetings.css"></style>

<style scoped>
.context-body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.25rem 1.5rem 1.5rem;
}

.context-save {
  min-width: 0;
}

.context-empty {
  padding: 2.5rem 1rem;
}

.context-empty__title {
  margin: 0 0 0.4rem;
  color: #111827;
  font-weight: 600;
}

.context-empty p {
  margin: 0 0 1rem;
}

.context-version-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.9rem;
  color: #4b5563;
  font-size: 0.8rem;
}

.context-version-line__label {
  padding: 0.1rem 0.55rem;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-weight: 600;
}

.context-version-line__words {
  color: #9ca3af;
}

.context-text {
  padding: 1.1rem 1.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.65rem;
  background: #ffffff;
  color: #111827;
  font-size: 14px;
  line-height: 1.6;
}

.context-editor__textarea {
  width: 100%;
  min-height: 28rem;
  padding: 0.9rem 1rem;
  border: 1px solid #2563eb;
  border-radius: 0.65rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85rem;
  line-height: 1.55;
  resize: vertical;
}

.context-editor__textarea:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.context-editor__hint {
  margin: 0.4rem 0 0;
  color: #6b7280;
  font-size: 0.8rem;
}

.context-history {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.context-history__table {
  min-width: 0;
}

.context-history__row--shown td {
  background: #f8fbff;
}

.context-history__actions {
  width: 8rem;
  text-align: right;
}

.context-history__current {
  color: #9ca3af;
  font-size: 0.8rem;
}

.meeting-table-wrap {
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.65rem;
}

.meeting-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.meeting-table th {
  padding: 12px 14px;
  background: #f9fafb;
  color: #4b5563;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: left;
  text-transform: uppercase;
}

.meeting-table td {
  padding: 10px 14px;
  border-top: 1px solid #e5e7eb;
  color: #111827;
  font-size: 14px;
  line-height: 1.25rem;
  vertical-align: top;
}

.md-content >>> h1,
.md-content >>> h2,
.md-content >>> h3 {
  margin: 1.1rem 0 0.4rem;
  color: #111827;
  font-weight: 600;
  line-height: 1.3;
}

.md-content >>> h1 { font-size: 1.125rem; }
.md-content >>> h2 { font-size: 1.05rem; }
.md-content >>> h3 { font-size: 1rem; }
.md-content >>> h1:first-child,
.md-content >>> h2:first-child { margin-top: 0; }
.md-content >>> p { margin: 0 0 0.7rem; }
.md-content >>> ul { margin: 0.4rem 0 0.8rem; padding-left: 1.3rem; list-style: disc; }
.md-content >>> ol { margin: 0.4rem 0 0.8rem; padding-left: 1.3rem; list-style: decimal; }
.md-content >>> li { margin: 0.2rem 0; }
.md-content >>> strong { font-weight: 600; }
.md-content >>> hr { margin: 1rem 0; border: 0; border-top: 1px solid #e5e7eb; }

@media (max-width: 768px) {
  .context-body {
    padding: 1rem 1.25rem 1.25rem;
  }
}
</style>
